import { ReadonlyVec2, ReadonlyVec3, vec2 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { ShaderProgram } from '~/engine/core/utils/ShaderProgram';
import { UniformFloat } from '~/engine/core/utils/uniforms/UniformFloat';
import { UniformVec2 } from '~/engine/core/utils/uniforms/UniformVec2';
import { UniformVec4 } from '~/engine/core/utils/uniforms/UniformVec4';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { Grid } from '../Grid';
import { CustomObject } from './CustomObject';

/**
 * Geometry of the bounding-box gizmo projected onto a 2D view,
 * expressed in canvas coordinates (pixels, origin top-left).
 */
export interface GizmoGeometry {
    /** Centre of the bounding rectangle. */
    center: ReadonlyVec2;
    /** Half-width and half-height of the bounding rectangle. */
    halfSize: ReadonlyVec2;
    /** Radius of the circumscribed circle (half-diagonal). */
    radius: number;
    /** Four corners of the bounding rectangle: TL, TR, BR, BL. */
    corners: readonly [ReadonlyVec2, ReadonlyVec2, ReadonlyVec2, ReadonlyVec2];
    /** Midpoints of each edge of the bounding rectangle. */
    edgeMidpoints: {
        top: ReadonlyVec2;
        bottom: ReadonlyVec2;
        left: ReadonlyVec2;
        right: ReadonlyVec2;
    };
}

/** Outline thickness in pixels. */
const LINE_WIDTH = 2.0;

/** Half-size of corner handle squares in pixels. */
const CORNER_HANDLE_SIZE = 5.0;

/** Radius of edge midpoint indicator circles in pixels. */
const EDGE_INDICATOR_RADIUS = 4.0;

/** Opacity of the footprint outline, against the gizmo's own 1. */
const FOOTPRINT_ALPHA = 0.55;

/**
 * A {@link CustomObject} that renders a bounding-box rectangle,
 * circumscribed circle, corner handles, and edge midpoint
 * indicators around a structure, and, faintly, the rectangle of
 * its footprint while it has one.
 *
 * The gizmo reads the structure's current {@link fullBoundingBox}
 * on every render call, so it automatically tracks grid changes
 * during scale / translate / rotate operations.
 */
export class TransformGizmo extends CustomObject {
    private static _shader?: GizmoShader;

    private static getShader(): GizmoShader {
        if (!TransformGizmo._shader) {
            TransformGizmo._shader = new GizmoShader();
        }
        return TransformGizmo._shader;
    }

    private readonly _roi: RegionOfInterest;

    /** @param _footprint  Patient-space points, outlined while it returns any */
    constructor(
        roi: RegionOfInterest,
        private readonly _footprint: () => readonly ReadonlyVec3[] | undefined
    ) {
        super();
        this._roi = roi;
    }

    public get grid(): Grid {
        const texture = this._roi.texture;
        if (!texture) {
            throw new Error('TransformGizmo requires an ROI with a texture');
        }
        return texture.textureGrid;
    }

    /**
     * Compute the 2D gizmo geometry (bounding rect + circle +
     * corner handles + edge midpoints) by projecting the 3D
     * bounding box onto {@link view}.
     *
     * @returns geometry in canvas coordinates, or `undefined`
     *          if the structure has no content.
     */
    public getGizmoGeometry(view: PlaneView): GizmoGeometry | undefined {
        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return undefined;
        }
        return this.geometryOf(view, texture.fullBoundingBox.getVertices());
    }

    /** The geometry of the rectangle that `vertices`, patient space, span on `view`. */
    private geometryOf(
        view: PlaneView,
        vertices: readonly ReadonlyVec3[]
    ): GizmoGeometry | undefined {
        if (vertices.length === 0) {
            return undefined;
        }

        // Project all 8 bounding-box corners to canvas pixels
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const v of vertices) {
            const c = view.canvasCoordsFromPatientCoords(v);
            minX = Math.min(minX, c[0]);
            minY = Math.min(minY, c[1]);
            maxX = Math.max(maxX, c[0]);
            maxY = Math.max(maxY, c[1]);
        }

        const center = vec2.fromValues((minX + maxX) / 2, (minY + maxY) / 2);
        const halfSize = vec2.fromValues((maxX - minX) / 2, (maxY - minY) / 2);
        const radius = vec2.length(halfSize);

        const corners = [
            vec2.fromValues(center[0] - halfSize[0], center[1] - halfSize[1]), // TL
            vec2.fromValues(center[0] + halfSize[0], center[1] - halfSize[1]), // TR
            vec2.fromValues(center[0] + halfSize[0], center[1] + halfSize[1]), // BR
            vec2.fromValues(center[0] - halfSize[0], center[1] + halfSize[1]), // BL
        ] as const;

        const edgeMidpoints = {
            top: vec2.fromValues(center[0], center[1] - halfSize[1]),
            bottom: vec2.fromValues(center[0], center[1] + halfSize[1]),
            left: vec2.fromValues(center[0] - halfSize[0], center[1]),
            right: vec2.fromValues(center[0] + halfSize[0], center[1]),
        };

        return { center, halfSize, radius, corners, edgeMidpoints };
    }

    /**
     * Render the bounding rectangle, circumscribed circle,
     * corner handles, and edge midpoint indicators using a
     * full-screen quad shader.
     */
    public render(view: TextureView): void {
        if (!(view instanceof PlaneView)) {
            return;
        }

        const geometry = this.getGizmoGeometry(view);
        if (!geometry) {
            return;
        }

        const { center, halfSize, radius } = geometry;
        const screenHeight = view.size[1];

        // Convert canvas coords (origin top-left, Y down) to
        // WebGL pixel coords (origin bottom-left, Y up)
        const glCenterX = center[0];
        const glCenterY = screenHeight - center[1];

        const color = this._roi.color;
        const shader = TransformGizmo.getShader();
        const footprint = this.geometryOf(view, this._footprint() ?? []);
        const [footprintX, footprintY] = footprint?.center ?? [0, 0];

        shader.start();
        shader.rectCenter.loadVec2(glCenterX, glCenterY);
        shader.rectHalfSize.loadVec2(halfSize);
        shader.footprintCenter.loadVec2(footprintX, screenHeight - footprintY);
        shader.footprintHalfSize.loadVec2(footprint?.halfSize ?? [0, 0]);
        shader.footprintAlpha.loadFloat(footprint ? FOOTPRINT_ALPHA : 0);
        shader.circleRadius.loadFloat(radius);
        shader.lineWidth.loadFloat(LINE_WIDTH);
        shader.cornerHandleSize.loadFloat(CORNER_HANDLE_SIZE);
        shader.edgeIndicatorRadius.loadFloat(EDGE_INDICATOR_RADIUS);
        // ROI color is vec3 (RGB normalised); pass with alpha = 1
        shader.color.loadVec4(color, 1.0);

        const vao = RenderStorage.getQuadVAO();
        vao.bind(0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        vao.unbind(0);

        shader.stop();
    }

    public delete(): void {
        // Shader is a shared static — nothing to clean up.
    }
}

/**
 * Shader that draws anti-aliased rectangle outlines, a
 * circumscribed circle, filled corner handle squares, and
 * filled edge-midpoint circles in pixel space using 2D
 * signed-distance functions.
 */
class GizmoShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;

    in vec2 position;

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;

    uniform vec2 rectCenter;
    uniform vec2 rectHalfSize;
    uniform vec2 footprintCenter;
    uniform vec2 footprintHalfSize;
    uniform float footprintAlpha;
    uniform float circleRadius;
    uniform float lineWidth;
    uniform float cornerHandleSize;
    uniform float edgeIndicatorRadius;
    uniform vec4 color;

    out vec4 outColor;

    // Dash pattern constants (pixels)
    const float DASH = 8.0;
    const float GAP = 6.0;
    const float PATTERN = DASH + GAP;
    const float FOOTPRINT_DASH = 3.0;
    const float FOOTPRINT_PATTERN = 7.0;

    // SDF for an axis-aligned box centred at the origin
    float sdBox(vec2 p, vec2 halfSz) {
        vec2 d = abs(p) - halfSz;
        return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
    }

    void main() {
        vec2 p = gl_FragCoord.xy;
        vec2 q = p - rectCenter;
        float halfW = lineWidth / 2.0;

        // ── Bounding rectangle (dashed outline) ──────────
        vec2 d = abs(q) - rectHalfSize;
        float rectDist = length(max(d, 0.0))
                       + min(max(d.x, d.y), 0.0);

        float rectPerim = (abs(d.x) > abs(d.y)) ? q.x : q.y;
        float rectDash = step(mod(rectPerim, PATTERN), DASH);

        float rectAlpha = rectDash * (1.0
            - smoothstep(halfW - 0.5, halfW + 0.5, abs(rectDist)));

        // ── Circumscribed circle (dashed outline) ────────
        float circleDist = length(q) - circleRadius;

        float angle = atan(q.y, q.x);
        float arcLen = angle * circleRadius;
        float circleDash = step(mod(arcLen, PATTERN), DASH);

        float circleAlpha = circleDash * (1.0
            - smoothstep(halfW - 0.5, halfW + 0.5, abs(circleDist)));

        // ── Corner handle squares (filled) ───────────────
        vec2 hs = rectHalfSize;
        float cHS = cornerHandleSize;
        vec2 cHandleSz = vec2(cHS);

        // Four corners relative to rect centre
        float c0 = sdBox(q - vec2(-hs.x, -hs.y), cHandleSz);
        float c1 = sdBox(q - vec2( hs.x, -hs.y), cHandleSz);
        float c2 = sdBox(q - vec2( hs.x,  hs.y), cHandleSz);
        float c3 = sdBox(q - vec2(-hs.x,  hs.y), cHandleSz);
        float cornerDist = min(min(c0, c1), min(c2, c3));
        float cornerAlpha = 1.0 - smoothstep(-0.5, 0.5, cornerDist);

        // ── Edge midpoint indicators (filled circles) ────
        float eR = edgeIndicatorRadius;

        float e0 = length(q - vec2(0.0, -hs.y)) - eR; // top
        float e1 = length(q - vec2(0.0,  hs.y)) - eR; // bottom
        float e2 = length(q - vec2(-hs.x, 0.0)) - eR; // left
        float e3 = length(q - vec2( hs.x, 0.0)) - eR; // right
        float edgeDist = min(min(e0, e1), min(e2, e3));
        float edgeAlpha = 1.0 - smoothstep(-0.5, 0.5, edgeDist);

        // ── Footprint rectangle (faint dashed outline) ──
        vec2 f = p - footprintCenter;
        vec2 fd = abs(f) - footprintHalfSize;
        float footprintPerim = (abs(fd.x) > abs(fd.y)) ? f.x : f.y;
        float footprintLine = footprintAlpha
            * step(mod(footprintPerim, FOOTPRINT_PATTERN), FOOTPRINT_DASH)
            * (1.0 - smoothstep(halfW - 0.5, halfW + 0.5,
                                abs(sdBox(f, footprintHalfSize))));

        // ── Combine all elements ─────────────────────────
        float alpha = max(max(rectAlpha, circleAlpha),
                          max(cornerAlpha, edgeAlpha));
        alpha = max(alpha, footprintLine);
        if (alpha < 0.01) {
            discard;
        }
        outColor = vec4(color.rgb, color.a * alpha);
    }
    `;

    public readonly rectCenter: UniformVec2;
    public readonly rectHalfSize: UniformVec2;
    public readonly footprintCenter: UniformVec2;
    public readonly footprintHalfSize: UniformVec2;
    public readonly footprintAlpha: UniformFloat;
    public readonly circleRadius: UniformFloat;
    public readonly lineWidth: UniformFloat;
    public readonly cornerHandleSize: UniformFloat;
    public readonly edgeIndicatorRadius: UniformFloat;
    public readonly color: UniformVec4;

    public constructor() {
        super(
            GizmoShader.VERTEX_CONTENT,
            GizmoShader.FRAGMENT_CONTENT,
            'position'
        );
        this.rectCenter = new UniformVec2('rectCenter');
        this.rectHalfSize = new UniformVec2('rectHalfSize');
        this.footprintCenter = new UniformVec2('footprintCenter');
        this.footprintHalfSize = new UniformVec2('footprintHalfSize');
        this.footprintAlpha = new UniformFloat('footprintAlpha');
        this.circleRadius = new UniformFloat('circleRadius');
        this.lineWidth = new UniformFloat('lineWidth');
        this.cornerHandleSize = new UniformFloat('cornerHandleSize');
        this.edgeIndicatorRadius = new UniformFloat('edgeIndicatorRadius');
        this.color = new UniformVec4('color');
        this.storeUniformLocations(
            this.rectCenter,
            this.rectHalfSize,
            this.footprintCenter,
            this.footprintHalfSize,
            this.footprintAlpha,
            this.circleRadius,
            this.lineWidth,
            this.cornerHandleSize,
            this.edgeIndicatorRadius,
            this.color
        );
    }
}
